import {
    ASX_IHub_AppFeedback_FormConfig,
    FeedbackFormModal,
    useFeedbackForm,
} from "@amzn/ads-feedback-kit";
import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { FeedbackForm, FormType } from "@amzn/aquila-feedback-web-assets";
import type { FormData } from "@amzn/aquila-service-type-script-client";
import {
    Flex,
    Icon,
    Menu,
    MenuButtonTrigger,
    MenuItemButton,
    Text,
} from "@amzn/storm-ui";
import { externalLink, feedback, lightbulb, plus } from "@amzn/storm-ui-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { IHUB_FEATURE_REQUEST_CTI } from "../../config/featureRequest.config";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../constants/telemetry";
import { authStore } from "../../store/auth";
import { navigationStore } from "../../store/navigation";
import { notificationStore, NotificationType } from "../../store/notification";
import { TopNavBarTabs } from "../../types/navigation";
import { logWithTakt } from "../../utils/telemetry.utils";
import { getBooleanFromString } from "../../utils/window.utils";
import { AllInsightsReportIssue } from "../aquilaReportIssue/AllInsightsReportIssue";
import { YourInsightsReportIssue } from "../aquilaReportIssue/YourInsightsReportIssue";
import { CreateFeedbackNotificationMessage } from "../common/notification.components";
import { AppCsatFeedbackFormRules } from "../csat/AppCsatHelper";

const StyledFeatureRequestFormWrapper = styled.div`
    textarea[data-testid="aquila-description-textarea"] {
        height: 200px;
        width: 400px;
    }
`;

export const ContactUs: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
    const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
    const [isFeatureRequestOpen, setIsFeatureRequestOpen] = useState(false);
    const { openFeedbackForm } = useFeedbackForm({
        formConfig: ASX_IHub_AppFeedback_FormConfig,
    });
    const [activeTab] = useStore(navigationStore, (state) => [state.activeTab]);
    const username = useStore(authStore, (state) => state.username);
    const aquilaConfig = useStore(
        authStore,
        (state) => state.settings.aquilaConfig,
    );
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);
    const setReportIssueOpenRef = useRef<(open: boolean) => void>();
    const [searchParams, setSearchParams] = useSearchParams();
    const showDirectCsatFeedback = getBooleanFromString(
        searchParams.get("showDirectCsatFeedback") ?? undefined,
    );

    const toggleMenu = () => setIsOpen(!isOpen);
    const closeMenu = () => setIsOpen(false);

    const onReportIssueClick = () => {
        logWithTakt(TELEMETRY_ID.CONTACT_US_REPORT_ISSUE_CLICKED);
        PhoenixMetrics.emit(
            TELEMETRY_ID.CONTACT_US_REPORT_ISSUE_CLICKED,
            PHOENIX_FEATURE.FEEDBACK,
        );
        setReportIssueOpenRef.current?.(true);
        closeMenu();
    };

    const onVOCFeedbackClick = () => {
        logWithTakt(TELEMETRY_ID.CONTACT_US_SHARE_FEEDBACK_FORM_CLICKED);
        PhoenixMetrics.emit(
            TELEMETRY_ID.CONTACT_US_SHARE_FEEDBACK_FORM_CLICKED,
            PHOENIX_FEATURE.FEEDBACK,
        );
        setIsFeedbackFormOpen(true);
        setTimeout(() => {
            openFeedbackForm();
        }, 100);
        closeMenu();
    };

    const onFeatureRequestClick = () => {
        setIsFeatureRequestOpen(true);
        logWithTakt(TELEMETRY_ID.CONTACT_US_FEATURE_REQUEST_CLICKED);
        PhoenixMetrics.emit(
            TELEMETRY_ID.CONTACT_US_FEATURE_REQUEST_CLICKED,
            PHOENIX_FEATURE.FEEDBACK,
        );
        closeMenu();
    };

    const onContentRequestClick = () => {
        logWithTakt(TELEMETRY_ID.CONTACT_US_CONTENT_REQUEST_CLICKED);
        PhoenixMetrics.emit(
            TELEMETRY_ID.CONTACT_US_CONTENT_REQUEST_CLICKED,
            PHOENIX_FEATURE.FEEDBACK,
        );
        window.open(
            "https://sim.amazon.com/issues/create?template=7260b589-22d5-422b-8ccf-8c0674f3c0a8",
            "_blank",
        );
        closeMenu();
    };

    const onUserGuideClick = () => {
        logWithTakt(TELEMETRY_ID.CONTACT_US_USER_GUIDE_CLICKED);
        PhoenixMetrics.emit(
            TELEMETRY_ID.CONTACT_US_USER_GUIDE_CLICKED,
            PHOENIX_FEATURE.FEEDBACK,
        );
        window.open("https://w.amazon.com/bin/view/InsightsHub/", "_blank");
        closeMenu();
    };

    const onFeedbackFormClose = useCallback(() => {
        setIsFeedbackFormOpen(false);
        if (!isFeedbackSubmitted) {
            logWithTakt(TELEMETRY_ID.CONTACT_US_SHARE_FEEDBACK_FORM_CLOSED, {
                showDirectCsatFeedback,
            });
            PhoenixMetrics.emit(
                TELEMETRY_ID.CONTACT_US_SHARE_FEEDBACK_FORM_CLOSED,
                PHOENIX_FEATURE.FEEDBACK,
                {
                    customData: {
                        showDirectCsatFeedback,
                    },
                },
            );
        }
    }, [isFeedbackSubmitted, showDirectCsatFeedback]);

    const onFeedbackFormSubmit = useCallback(() => {
        logWithTakt(TELEMETRY_ID.CONTACT_US_SHARE_FEEDBACK_FORM_SUBMITTED, {
            showDirectCsatFeedback,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.CONTACT_US_SHARE_FEEDBACK_FORM_SUBMITTED,
            PHOENIX_FEATURE.FEEDBACK,
            {
                customData: {
                    showDirectCsatFeedback,
                },
            },
        );
        setIsFeedbackFormOpen(true);
        setIsFeedbackSubmitted(true);
    }, [showDirectCsatFeedback]);

    const onAllInsightsReportIssueComponentReady = (data) => {
        const { setIsReportIssueOpen: setIsAllInsightsReportIssueOpen } = data;
        setReportIssueOpenRef.current = setIsAllInsightsReportIssueOpen;
    };

    const onYourInsightsReportIssueComponentReady = (data) => {
        const { setIsReportIssueOpen: setIsYourInsightsReportIssueOpen } = data;
        setReportIssueOpenRef.current = setIsYourInsightsReportIssueOpen;
    };

    const handleOnFeatureRequestSubmit = (
        formData: FormData[],
        callback,
        additionalInfo,
    ) => {
        logWithTakt(TELEMETRY_ID.CONTACT_US_FEATURE_REQUEST_SUBMITTED);
        PhoenixMetrics.emit(
            TELEMETRY_ID.CONTACT_US_FEATURE_REQUEST_SUBMITTED,
            PHOENIX_FEATURE.FEEDBACK,
        );
        setIsFeatureRequestOpen(false);
        if (callback) {
            callback({
                formData,
                additionalFormContext: [],
                ticketData: {
                    cti: IHUB_FEATURE_REQUEST_CTI,
                    tags: [],
                    title: `IHub Feature Request - ${username}`,
                },
            })
                .then((ticketId) => {
                    if (ticketId) {
                        upsertNotification(
                            <CreateFeedbackNotificationMessage
                                ticketId={ticketId}
                            />,
                            NotificationType.SUCCESS,
                        );
                    }
                })
                .catch(() => {
                    upsertNotification(
                        "There was an error submitting your feature request. Please try again later.",
                        NotificationType.ERROR,
                    );
                });
        }
    };

    const handleOnFeatureRequestCancel = () => {
        logWithTakt(TELEMETRY_ID.CONTACT_US_FEATURE_REQUEST_CANCELLED);
        PhoenixMetrics.emit(
            TELEMETRY_ID.CONTACT_US_FEATURE_REQUEST_CANCELLED,
            PHOENIX_FEATURE.FEEDBACK,
        );
        setIsFeatureRequestOpen(false);
    };

    useEffect(() => {
        if (showDirectCsatFeedback) {
            const newSearchParams = new URLSearchParams(searchParams);
            setIsFeedbackFormOpen(true);
            setTimeout(() => {
                openFeedbackForm();
            }, 100);
            newSearchParams.delete("showDirectCsatFeedback");
            setSearchParams(newSearchParams);
        }
    }, [showDirectCsatFeedback]);

    return (
        <>
            <Menu
                id="contact-us-menu"
                onClick={toggleMenu}
                onClose={closeMenu}
                isOpen={isOpen}
                trigger={<MenuButtonTrigger>Contact us</MenuButtonTrigger>}
            >
                <MenuItemButton onClick={onReportIssueClick}>
                    <Flex alignItems="center" columnGap="xxsmall">
                        <Icon type="bug" />
                        <Text>Report an Issue</Text>
                    </Flex>
                </MenuItemButton>
                <MenuItemButton onClick={onVOCFeedbackClick}>
                    <Flex alignItems="center" columnGap="xxsmall">
                        <Icon type={feedback} />
                        Share Feedback
                    </Flex>
                </MenuItemButton>
                <MenuItemButton onClick={onFeatureRequestClick}>
                    <Flex alignItems="center" columnGap="xxsmall">
                        <Icon type={lightbulb} />
                        Feature Request
                    </Flex>
                </MenuItemButton>
                <MenuItemButton onClick={onContentRequestClick}>
                    <Flex alignItems="center" columnGap="xxsmall">
                        <Icon type={plus} />
                        New Content Ideas
                    </Flex>
                </MenuItemButton>
                <MenuItemButton onClick={onUserGuideClick}>
                    <Flex alignItems="center" columnGap="xxsmall">
                        <Icon type={externalLink} />
                        User Guide
                    </Flex>
                </MenuItemButton>
            </Menu>
            {isFeedbackFormOpen && (
                <FeedbackFormModal
                    formConfig={ASX_IHub_AppFeedback_FormConfig}
                    locale={"en-US"}
                    rules={AppCsatFeedbackFormRules}
                    allowMultiple={false}
                    onClose={onFeedbackFormClose}
                    onSubmit={onFeedbackFormSubmit}
                />
            )}
            {activeTab === TopNavBarTabs.YOUR_INSIGHT_DECKS ? (
                <YourInsightsReportIssue
                    onComponentReady={onYourInsightsReportIssueComponentReady}
                />
            ) : (
                <AllInsightsReportIssue
                    onComponentReady={onAllInsightsReportIssueComponentReady}
                />
            )}
            {isFeatureRequestOpen && aquilaConfig.IHubFeatureRequestConfig && (
                <StyledFeatureRequestFormWrapper>
                    <FeedbackForm
                        isOpen={isFeatureRequestOpen}
                        formType={FormType.MODAL}
                        configId={
                            aquilaConfig.IHubFeatureRequestConfig.configId
                        }
                        username={username}
                        onSubmit={handleOnFeatureRequestSubmit}
                        onClose={handleOnFeatureRequestCancel}
                        loaderNode={<></>}
                    />
                </StyledFeatureRequestFormWrapper>
            )}
        </>
    );
};
